/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { API_BASE_URL } from '../utils/apiBaseUrl.ts';

// The only shape this hook needs from a payout record - deliberately
// loose so it accepts both AdminPayouts' local PayoutRecord and the one
// from payoutService.ts (used by UserPayouts) without coupling the two.
export interface PayoutLike {
  collectionHandle?: string;
  collectionTitle: string;
  orders: Array<{ number: string }>;
  lastOrderNumberPaid?: number;
}

export interface DateRange {
  start: Date;
  end: Date;
}

interface UseUnpaidOrdersInRangeResult {
  orders: any[];
  loading: boolean;
  error: boolean;
}

// Fetches every order placed in the given collection within `range`, then
// filters out whichever of those orders is already covered by a payout.
// The backend endpoint does real work per order (matching Printful/Yoycol
// fulfillment costs), so a narrower range is a genuinely faster request,
// not just a smaller one - this is the shared low-level fetch both
// useEarnedSincePayout (full history, for the amount-owed figure) and
// useRecentUnpaidOrders (a recent window, for the dashboard charts) build
// on with different ranges.
export function useUnpaidOrdersInRange(
  selectedHandle: string | undefined,
  payouts: PayoutLike[],
  payoutsLoading: boolean,
  range: DateRange,
  // When given, takes over from `range` entirely: the backend scopes by
  // order_number > minOrderNumber instead of by date, sidestepping the
  // date-string parsing issues a free-text payout date can hit (see
  // parseDateRobust in formatters.ts). useRecentUnpaidOrders (the
  // dashboard charts' fast path) never passes this - it genuinely needs
  // a fixed recent window, not an order-number cutoff.
  minOrderNumber?: number | null
): UseUnpaidOrdersInRangeResult {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // range is passed as a fresh-looking object by some callers (built from
  // `new Date()`), so key off its millisecond bounds rather than object
  // identity - a stable primitive the effect can safely depend on.
  const rangeKey = `${range.start.getTime()}-${range.end.getTime()}-${minOrderNumber ?? 'none'}`;

  useEffect(() => {
    if (!selectedHandle) {
      setOrders([]);
      setError(false);
      return;
    }

    // Wait for the payouts list before computing anything - it's how
    // paid status gets determined, so running before it's loaded would
    // treat every order as unpaid.
    if (payoutsLoading) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(false);

      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('No authenticated user');
        }
        const token = await currentUser.getIdToken();

        const url = `${API_BASE_URL}/.netlify/functions/get-collection-sales-data-from-firestore`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(
            typeof minOrderNumber === 'number' && !isNaN(minOrderNumber)
              ? { collectionHandle: selectedHandle, minOrderNumber }
              : {
                  collectionHandle: selectedHandle,
                  dateRange: {
                    start: range.start.toISOString(),
                    end: range.end.toISOString()
                  }
                }
          )
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch orders (${response.status}): ${errorText.slice(0, 300)}`);
        }

        const result = await response.json();
        const fetched: any[] = result?.data?.orders || [];

        const paidOrderNumbers = new Set<string>();
        payouts.forEach((payout) => {
          payout.orders.forEach((order) => {
            if (order.number) {
              paidOrderNumbers.add(String(order.number));
            }
          });
        });

        const stillUnpaid = fetched.filter((order) => {
          let num = String(order.orderNumber);
          if (num.startsWith('#')) {
            num = num.substring(1);
          }
          return !paidOrderNumbers.has(num);
        });

        if (!cancelled) {
          setOrders(stillUnpaid);
        }
      } catch (err) {
        console.error('Error fetching unpaid orders in range:', err);
        if (!cancelled) {
          setOrders([]);
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHandle, payouts, payoutsLoading, rangeKey]);

  return { orders, loading, error };
}
